// Professional Voicetrack Interface JavaScript
class VoicetrackInterface {
  constructor() {
    this.currentTrack = null;
    this.isRecording = false;
    this.isPlaying = false;
    this.wavesurfer = null;
    this.recordRTC = null;
    
    this.initializeInterface();
    this.bindEvents();
  }
  
  initializeInterface() {
    console.info('Professional Voicetrack Interface initialized');
    this.setupAudioContext();
    this.setupWaveSurfer();
    this.updateMicrophoneList();
  }
  
  bindEvents() {
    // Bind control buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('.btn-voicetrack')) {
        this.toggleRecording();
      } else if (e.target.closest('.btn-stop')) {
        this.stopAll();
      } else if (e.target.closest('.btn-play')) {
        this.playAll();
      } else if (e.target.closest('.btn-align')) {
        this.alignTracks();
      } else if (e.target.closest('.btn-insert')) {
        this.insertTrack();
      } else if (e.target.closest('.btn-load')) {
        this.loadTrack();
      } else if (e.target.closest('.btn-next')) {
        this.nextTrack();
      } else if (e.target.closest('.btn-copy')) {
        this.copyFeedback();
      }
      
      // Show selection events
      if (e.target.closest('.show-entry')) {
        const showEntry = e.target.closest('.show-entry');
        this.selectShow(showEntry);
      }
      
      // Track selection from log
      if (e.target.closest('.log-item')) {
        const trackElement = e.target.closest('.log-item');
        this.loadTrackFromLog(trackElement);
      }
    });
    
    // Keyboard shortcuts
    document.addEventListener('keydown', (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch(e.key) {
          case 'r':
            e.preventDefault();
            this.toggleRecording();
            break;
          case ' ':
            e.preventDefault();
            this.playAll();
            break;
          case 's':
            e.preventDefault();
            // Cmd+S (Mac metaKey) is reserved for Save Voicetrack; only stop on Ctrl+S
            if (e.ctrlKey) {
              this.stopAll();
            }
            break;
        }
      }
    });
  }
  
  setupAudioContext() {
    if (typeof AudioContext !== 'undefined' || typeof webkitAudioContext !== 'undefined') {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
  }
  
  setupWaveSurfer() {
    if (typeof WaveSurfer !== 'undefined') {
      const container = document.querySelector('#record-elements');
      if (container) {
        // Create waveform container
        const waveformDiv = document.createElement('div');
        waveformDiv.id = 'waveform-display';
        waveformDiv.style.cssText = 'height: 80px; width: 100%; background: #f3f4f6; border-radius: 8px; margin: 10px 0;';
        container.appendChild(waveformDiv);
        
        this.wavesurfer = WaveSurfer.create({
          container: '#waveform-display',
          waveColor: '#3b82f6',
          progressColor: '#1d4ed8',
          height: 80,
          responsive: true,
          normalize: true
        });
        
        container.style.display = 'block';
      }
    }
  }
  
  // Update microphone list
  async updateMicrophoneList() {
    try {
      // Check for MediaDevices API support
      if (!navigator.mediaDevices) {
        this.showMicrophoneUnavailable('Microphone requires HTTPS for secure access');
        return;
      }

      if (!navigator.mediaDevices.enumerateDevices) {
        this.showMicrophoneUnavailable('Device enumeration not supported in this browser');
        return;
      }

      // Request microphone permission first to get device labels
      let permissionGranted = false;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop()); // Stop the stream immediately
        permissionGranted = true;
      } catch (permError) {
        console.info('Microphone permission not granted, device labels may be limited');
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(device => device.kind === 'audioinput');
      
      const micSelect = document.getElementById('mic-select');
      if (micSelect) {
        micSelect.innerHTML = '<option value="" hidden>Select Microphone</option>';
        
        if (audioInputs.length === 0) {
          this.showMicrophoneUnavailable('No microphones found');
        } else {
          audioInputs.forEach((device, index) => {
            const option = document.createElement('option');
            option.value = device.deviceId;
            option.textContent = device.label || `Microphone ${index + 1}`;
            micSelect.appendChild(option);
          });
          micSelect.style.display = 'block';
          console.log(`Found ${audioInputs.length} audio input device(s)`);
        }
      }
    } catch (error) {
      console.warn('Error accessing microphone devices:', error);
      this.showMicrophoneUnavailable('Unable to access microphone devices');
    }
  }

  // Show microphone unavailable message
  showMicrophoneUnavailable(message) {
    const micSelect = document.getElementById('mic-select');
    if (micSelect) {
      micSelect.innerHTML = `<option disabled>${message}</option>`;
      micSelect.style.display = 'block';
      micSelect.disabled = true;
    }
    console.warn('Microphone unavailable:', message);
  }
  
  async toggleRecording() {
    if (this.isRecording) {
      this.stopRecording();
    } else {
      await this.startRecording();
    }
  }
  
  async startRecording() {
    try {
      // Check if MediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('MediaDevices API not available in this browser');
      }
      
      // Check for MediaRecorder support
      if (!window.MediaRecorder) {
        throw new Error('MediaRecorder not supported in this browser');
      }
      
      const micSelect = document.getElementById('mic-select');
      const deviceId = micSelect?.value;
      
      const constraints = {
        audio: deviceId ? { deviceId: { exact: deviceId } } : true,
        video: false
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      // Visual feedback
      this.updateRecordingState(true);
      
      if (this.wavesurfer && typeof WaveSurfer.microphone !== 'undefined') {
        this.wavesurfer.microphone.start();
      }
      
      this.mediaStream = stream;
      console.log('Recording started successfully');
      
    } catch (error) {
      console.error('Failed to start recording:', error);
      
      let errorMessage = 'Failed to access microphone.';
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Microphone access denied. Please allow microphone access and try again.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No microphone found. Please connect a microphone and try again.';
      } else if (error.message.includes('not available') || error.message.includes('not supported')) {
        errorMessage = 'Your browser does not support audio recording features.';
      } else {
        errorMessage = 'Unable to start recording. Please check your microphone settings.';
      }
      
      this.showError(errorMessage);
    }
  }
  
  stopRecording() {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }
    
    if (this.wavesurfer && this.wavesurfer.microphone) {
      this.wavesurfer.microphone.stop();
    }
    
    this.updateRecordingState(false);
    console.log('Recording stopped');
  }
  
  stopAll() {
    this.stopRecording();
    if (this.wavesurfer && this.wavesurfer.isPlaying()) {
      this.wavesurfer.stop();
    }
    this.updatePlayingState(false);
  }
  
  playAll() {
    if (this.wavesurfer) {
      if (this.wavesurfer.isPlaying()) {
        this.wavesurfer.pause();
        this.updatePlayingState(false);
      } else {
        this.wavesurfer.play();
        this.updatePlayingState(true);
      }
    }
  }
  
  alignTracks() {
    console.log('Align tracks functionality');
    // Implement track alignment logic
  }
  
  insertTrack() {
    console.log('Insert track functionality');
    // Implement track insertion logic
  }
  
  loadTrack() {
    console.log('Load track functionality');
    // Implement track loading logic
  }
  
  nextTrack() {
    console.log('Next track functionality');
    // Implement next track logic
  }
  
  copyFeedback() {
    // Copy current feedback to clipboard
    const feedbackText = this.generateFeedback();
    navigator.clipboard.writeText(feedbackText).then(() => {
      this.showSuccess('Feedback copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy feedback:', err);
    });
  }
  
  selectShow(showElement) {
    // Remove previous selection
    document.querySelectorAll('.show-entry.selected').forEach(el => {
      el.classList.remove('selected');
    });
    
    // Add selection to clicked show
    showElement.classList.add('selected');
    
    // Update interface with show data
    this.loadShowData(showElement);
  }
  
  loadTrackFromLog(trackElement) {
    // Load track data into voicetrack interface
    const trackData = this.extractTrackData(trackElement);
    this.currentTrack = trackData;
    
    // Update track info panels
    this.updateTrackInfo(trackData);
    
    // Visual feedback
    document.querySelectorAll('.log-item.active').forEach(el => {
      el.classList.remove('active');
    });
    trackElement.classList.add('active');
  }
  
  loadShowData(showElement) {
    const vtIndex = showElement.dataset.vtIndex;
    if (vtIndex) {
      // Load voicetrack data via AJAX
      console.log(`Loading voicetrack ${vtIndex}`);
      // Implement AJAX call to load voicetrack data
    }
  }
  
  extractTrackData(trackElement) {
    return {
      index: trackElement.dataset.index,
      title: trackElement.querySelector('.title')?.textContent?.trim(),
      artist: trackElement.querySelector('.artist')?.textContent?.trim(),
      time: trackElement.querySelector('.time')?.textContent?.trim(),
      duration: trackElement.querySelector('.duration')?.textContent?.trim()
    };
  }
  
  updateTrackInfo(trackData) {
    // Update track header section
    const headerSection = document.getElementById('track-header-section');
    if (headerSection && trackData) {
      // Update with track information
      console.log('Updating track info:', trackData);
    }
  }
  
  updateRecordingState(isRecording) {
    this.isRecording = isRecording;
    const recordBtn = document.querySelector('.btn-voicetrack');
    
    if (recordBtn) {
      if (isRecording) {
        recordBtn.classList.add('recording');
        recordBtn.style.backgroundColor = '#dc2626';
        recordBtn.querySelector('.btn-label').textContent = 'Recording...';
      } else {
        recordBtn.classList.remove('recording');
        recordBtn.style.backgroundColor = '#059669';
        recordBtn.querySelector('.btn-label').textContent = 'Voice Track';
      }
    }
  }
  
  updatePlayingState(isPlaying) {
    this.isPlaying = isPlaying;
    const playBtn = document.querySelector('.btn-play');
    
    if (playBtn) {
      if (isPlaying) {
        playBtn.style.backgroundColor = '#dc2626';
        playBtn.querySelector('.btn-label').textContent = 'Playing...';
      } else {
        playBtn.style.backgroundColor = '#2563eb';
        playBtn.querySelector('.btn-label').textContent = 'Play All';
      }
    }
  }
  
  generateFeedback() {
    return `Voicetrack Feedback - ${new Date().toLocaleString()}\n` +
           `Track: ${this.currentTrack?.title || 'N/A'}\n` +
           `Artist: ${this.currentTrack?.artist || 'N/A'}\n` +
           `Time: ${this.currentTrack?.time || 'N/A'}\n`;
  }
  
  showSuccess(message) {
    this.showNotification(message, 'success');
  }
  
  showError(message) {
    this.showNotification(message, 'error');
  }
  
  showNotification(message, type = 'info') {
    // Create and show notification
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 ${
      type === 'success' ? 'bg-green-500 text-white' :
      type === 'error' ? 'bg-red-500 text-white' :
      'bg-blue-500 text-white'
    }`;
    notification.textContent = message;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.remove();
    }, 3000);
  }
}

// Global functions for backwards compatibility
function loadVoicetrack(index) {
  if (window.vtInterface) {
    const showElement = document.querySelector(`[data-vt-index="${index}"]`);
    if (showElement) {
      window.vtInterface.selectShow(showElement);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  if (window.__xtrackerVoicetrackInterfaceDisabledLogPrinted) {
    return;
  }
  window.__xtrackerVoicetrackInterfaceDisabledLogPrinted = true;
  console.log('VoicetrackInterface initialization disabled to prevent conflicts');
  // window.vtInterface = new VoicetrackInterface();
});

// Add selected state styling
const selectedShowStyle = document.createElement('style');
selectedShowStyle.textContent = `
  .show-entry.selected {
    background-color: #dbeafe !important;
    border-color: #3b82f6 !important;
  }
  
  .log-item.active {
    background-color: #fef3c7 !important;
    border-left: 4px solid #f59e0b !important;
  }
  
  .btn-control.recording {
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
`;
document.head.appendChild(selectedShowStyle);